import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { SelectedDay } from '~/subdomains/CalenderOverview/updateSelectionLogic';
import { ID, unwrap } from '../common/apiCommons';
import axios, { AxiosError } from 'axios';

export const OffDayTypes = {
  HOLIDAY: 1,
  SICK: 2,
  PUBLIC_HOLIDAY: 3,
  OTHER: 4,
};

export type OffDayType = keyof typeof OffDayTypes;

export type OffDay = {
  id: number;
  employee?: ID;
  type: OffDayType;
  name: string;
  date: Date;
  created: Date;
  updated: Date;
};

export type AddOffDaysRequest = {
  selectedDays: SelectedDay[];
  type: OffDayType;
  name: string;
};

export type DeleteOffDaysRequest = {
  selectedDays: SelectedDay[];
};

export type WorkDay = {
  employee_id: number;
  date: Date;
  is_working: boolean;
};

const queries = {
  async fetchWorkDays(date: Date) {
    const res = await axios.get<WorkDay[]>(
      `/workdays/${date.getFullYear()}/${date.getMonth() + 1}/`,
    );
    const dates = res.data;
    return dates.map((d) => ({
      ...d,
      date: new Date(d.date),
    }));
  },
  async fetchOffDays(date: Date) {
    const res = await axios.get<OffDay[]>(
      `/offdays/${date.getFullYear()}/${date.getMonth() + 1}/`,
    );
    const dates = unwrap(res);
    return dates.map((d) => {
      d.created = new Date(d.created);
      d.date = new Date(d.date);
      d.updated = new Date(d.updated);
      return d;
    });
  },
  async fetchOffDaysForYear(year: number) {
    const res = await axios.get<OffDay[]>(`/offdays/${year}/`);
    const dates = unwrap(res);
    return dates.map((d) => {
      d.created = new Date(d.created);
      d.date = new Date(d.date);
      d.updated = new Date(d.updated);
      return d;
    });
  },
};

const mutations = {
  async deleteOffDays(request: DeleteOffDaysRequest) {
    const res = await axios.delete<void>(`/offdays/`, { data: request });
    return unwrap(res);
  },
  async addOffDay(request: AddOffDaysRequest) {
    const res = await axios.post<OffDay[]>(`/offdays/`, request);
    return unwrap(res);
  },
};

const hooks = {
  useOffDaysOfYear<T = OffDay[]>(
    year: number,
    options?: UseQueryOptions<OffDay[], AxiosError, T>,
  ) {
    return useQuery({
      queryKey: ['offdays', year],
      queryFn: () => queries.fetchOffDaysForYear(year),
      ...(options ?? {}),
    });
  },
  useOffDaysOfMonth<T = OffDay[]>(
    date: Date,
    options?: UseQueryOptions<OffDay[], AxiosError, T>,
  ) {
    return useQuery({
      queryKey: ['offdays', date],
      queryFn: () => queries.fetchOffDays(date),
      ...(options ?? {}),
    });
  },
  useWorkDaysOfMonth<T = WorkDay[]>(
    date: Date,
    options?: UseQueryOptions<WorkDay[], AxiosError, T>,
  ) {
    return useQuery({
      queryKey: ['workdays', date],
      queryFn: () => queries.fetchWorkDays(date),
      ...(options ?? {}),
    });
  },
  useDeleteOffDays() {
    const queryClient = useQueryClient();
    return useMutation(mutations.deleteOffDays, {
      async onSuccess() {
        await queryClient.invalidateQueries(['offdays']);
      },
    });
  },
  useAddOffDay() {
    const queryClient = useQueryClient();
    return useMutation(mutations.addOffDay, {
      async onSuccess() {
        await queryClient.invalidateQueries(['offdays']);
      },
    });
  },
};

const TimetrackingApi = {
  queries,
  mutations,
  hooks,
};

export default TimetrackingApi;
